<!-- eslint-disable perfectionist/sort-modules -->
<!-- eslint-disable perfectionist/sort-imports -->
<!-- eslint-disable no-unused-vars -->
<script setup lang="ts">
import range from 'lodash/range';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  FunnelX,
  Search,
  X,
} from 'lucide-vue-next';
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { toast } from 'vue3-toastify';

import { formatCnpjTyping } from '@solfacil/frontend-utils/cnpj';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import useScrollToTop from '~/hooks/useScrollToTop';
import useAuth from '~/store/auth/useAuth';
import date from '~/utils/date';
import { DATE_FORMATS, PAGE_SIZE_OPTIONS } from '~/constants';

import { formatCompetencyMonth } from '../utils/formatHelpers';
import {
  formatCNPJForDisplay,
  formatNameForDisplay,
  formatPhoneForDisplay,
} from '~/utils/formatters';

import useTableSorting from '../hooks/useTableSorting';
import {
  PROCESSING_STATUS_OPTIONS, STATUS_SOLFACIL_OPTIONS, TABLE_COLUMNS,
} from '../constants';
import useInactiveAccountsFilters from '../hooks/useInactiveAccountsFilters';
import { useInactiveAccounts } from '../queries/useInactiveAccounts';
import useBatchProcessAccounts from '../queries/useBatchProcessAccounts';
import {
  getProcessingStatusColorClass,
  getProcessingStatusLabel,
  getSolfacilStatusColorClass,
  getSolfacilStatusLabel,
} from '../utils/statusHelpers';

const selectedAccounts = ref<string[]>([]);
const selectAll = ref(false);
const showConfirmModal = ref(false);
const pendingAction = ref<'approve' | 'reject' | null>(null);
const isProcessing = ref(false);

function clearSelections() {
  selectedAccounts.value = [];
  selectAll.value = false;
}

const {
  apiParams,
  filterForm,
  handleReset: originalHandleReset,
  handleSearch: originalHandleSearch,
  initializeForm,
  setPage,
  setPageSize,
} = useInactiveAccountsFilters({
  onFiltersChange: () => clearSelections(),
});
const [competencyMonthModel, competencyMonthProps] = filterForm.defineField('competency_month');
const [processingStatusModel, processingStatusProps] = filterForm.defineField('processing_status');
const [partnerCnpjModel, partnerCnpjProps] = filterForm.defineField('partner_cnpj');
const [partnerIdModel, partnerIdProps] = filterForm.defineField('partner_id');
const [statusSolfacilModel, statusSolfacilProps] = filterForm.defineField('status_solfacil');

const {
  data: accounts,
  isLoading: isLoadingAccounts,
  refetch: refetchAccounts,
} = useInactiveAccounts(apiParams);

const {
  handleSort,
  sortedAccounts,
  sortState,
} = useTableSorting(() => accounts.value?.inactive_bank_accounts);

const batchProcessMutation = useBatchProcessAccounts();
const { data: authData } = useAuth();

const hasNext = computed(() => !!accounts.value?.has_next);
const pageSize = computed(() => Number(apiParams.value.page_size) || 20);
const currentPage = computed(() => Number(apiParams.value.page) || 1);

function handlePageSizeChange(event: Event) {
  const target = event.target as HTMLSelectElement;
  const newPageSize = Number(target.value);

  clearSelections();

  setPageSize(newPageSize);
}

const canProcessSelectedAccounts = computed(() => {
  if (selectedAccounts.value.length === 0) return false;
  const selectedAccountsData = sortedAccounts.value.filter(
    (account) => selectedAccounts.value.includes(account.account_key),
  );
  return selectedAccountsData.every(
    (account) => account.processing_status === 'PENDING_APPROVAL',
  );
});

async function confirmAction() {
  if (!pendingAction.value) return;

  isProcessing.value = true;

  try {
    const result = await batchProcessMutation.mutateAsync({
      account_keys: selectedAccounts.value,
      action: pendingAction.value === 'approve' ? 'APPROVE' : 'REJECT',
    });

    toast.success('Contas processadas com sucesso!');
    clearSelections();
    await refetchAccounts();
  } catch (error) {
    toast.error('Erro ao processar contas. Tente novamente.');
  } finally {
    isProcessing.value = false;
    showConfirmModal.value = false;
    pendingAction.value = null;
  }
}

function handleSearch() {
  clearSelections();

  originalHandleSearch();
}

function handleReset() {
  clearSelections();

  originalHandleReset();
}

function handleCNPJInput(event: Event) {
  const input = event.target as HTMLInputElement;
  partnerCnpjModel.value = formatCnpjTyping(input.value);
}

function handleAccountSelect(accountKey: string) {
  const index = selectedAccounts.value.indexOf(accountKey);
  if (index > -1) {
    selectedAccounts.value.splice(index, 1);
  } else {
    selectedAccounts.value.push(accountKey);
  }
}

function handleSelectAll() {
  if (selectAll.value) {
    selectedAccounts.value = sortedAccounts.value.map((account) => account.account_key);
  } else {
    selectedAccounts.value = [];
  }
}

function openConfirmModal(action: 'approve' | 'reject') {
  if (selectedAccounts.value.length === 0) return;
  pendingAction.value = action;
  showConfirmModal.value = true;
}

function handleSetPage(value: number) {
  if (hasNext.value || value < Number(apiParams.value.page)) {
    clearSelections();

    setPage(value);
  }
}

watch(selectedAccounts, () => {
  selectAll.value = selectedAccounts.value.length === sortedAccounts.value.length && sortedAccounts.value.length > 0;
}, { deep: true });

useScrollToTop(currentPage);

onMounted(() => {
  document.title = 'Contas Inativas';
  initializeForm();
});
</script>

<template>
  <DashboardLayout title="Contas Inativas" description="Lista de contas sem movimentação">
    <div class="row row-cards">
      <div class="col-12">
        <div class="card position-relative">
          <div class="card-body border-bottom py-3">
            <div class="d-flex align-items-end gap-2 flex-wrap">
              <div style="min-width: 140px;">
                <div class="mb-3">
                  <label class="form-label" for="competency_month">Mês de Competência *</label>
                  <input
                    id="competency_month"
                    v-model="competencyMonthModel"
                    v-bind="competencyMonthProps"
                    type="month"
                    class="form-control"
                    required
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div style="min-width: 160px;">
                <div class="mb-3">
                  <label class="form-label" for="processing_status">Status Processamento *</label>
                  <select
                    id="processing_status"
                    v-model="processingStatusModel"
                    v-bind="processingStatusProps"
                    class="form-select"
                    required
                    aria-label="Campo de seleção de status de processamento"
                  >
                    <option
                      v-for="option in PROCESSING_STATUS_OPTIONS"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div style="min-width: 140px;">
                <div class="mb-3">
                  <label class="form-label" for="partner_cnpj">CNPJ</label>
                  <input
                    id="partner_cnpj"
                    v-model="partnerCnpjModel"
                    v-bind="partnerCnpjProps"
                    type="text"
                    class="form-control"
                    placeholder="00.000.000/0000-00"
                    maxlength="18"
                    @input="handleCNPJInput"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div style="min-width: 100px;">
                <div class="mb-3">
                  <label class="form-label" for="partner_id">ID do parceiro</label>
                  <input
                    id="partner_id"
                    v-model="partnerIdModel"
                    v-bind="partnerIdProps"
                    type="text"
                    class="form-control"
                    placeholder="ID do parceiro"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div style="min-width: 110px;">
                <div class="mb-3">
                  <label class="form-label" for="status_solfacil">Status Solfácil</label>
                  <select
                    id="status_solfacil"
                    v-model="statusSolfacilModel"
                    v-bind="statusSolfacilProps"
                    class="form-select"
                    aria-label="Campo de seleção de status solfácil"
                  >
                    <option value="">Todos</option>
                    <option
                      v-for="option in STATUS_SOLFACIL_OPTIONS"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div style="min-width: 200px;">
                <div class="d-flex gap-2 mb-3">
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="handleSearch"
                  >
                    <Search class="icon icon-sm" />
                    Buscar
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="handleReset"
                  >
                    <FunnelX class="icon icon-sm" />
                    Limpar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="selectedAccounts.length > 0" class="card-body border-bottom py-2 bg-yellow-lt">
          <div class="d-flex align-items-center justify-content-between px-4">
            <span class="text-black fw-bold">
              {{ selectedAccounts.length }} conta(s) selecionada(s)
            </span>
            <div class="d-flex gap-2">
              <button
                type="button"
                @click="openConfirmModal('approve')"
                :disabled="!canProcessSelectedAccounts || isProcessing"
                class="btn btn-success"
                :title="!canProcessSelectedAccounts
                  ? 'Apenas contas com status Pendente aprovação podem ser aprovadas'
                  : ''"
              >
                <Check class="icon icon-sm" />
                Aprovar
              </button>
              <button
                type="button"
                @click="openConfirmModal('reject')"
                :disabled="!canProcessSelectedAccounts || isProcessing"
                class="btn btn-danger"
                :title="!canProcessSelectedAccounts
                  ? 'Apenas contas com status Pendente aprovação podem ser reprovadas'
                  : ''"
              >
                <X class="icon icon-sm" />
                Reprovar
              </button>
            </div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-vcenter">
            <thead>
              <tr>
                <th scope="col" class=" ">
                  <div class="form-check mb-0">
                    <input
                      id="selectAllCheckbox"
                      v-model="selectAll"
                      @change="handleSelectAll"
                      type="checkbox"
                      class="form-check-input"
                      :disabled="sortedAccounts.length === 0"
                    />
                  </div>
                </th>
                <th
                  v-for="column in TABLE_COLUMNS"
                  :key="column.value"
                  scope="col"
                  :class="{
                    'col-cnpj': column.value === 'partner_cnpj',
                    'col-nome': column.value === 'partner_name',
                    'col-email': column.value === 'partner_email',
                    'col-telefone': column.value === 'partner_phone',
                  }"
                >
                  <div class="d-flex align-items-center justify-content-between break-anywhere">
                    <span>{{ column.name }}</span>
                    <div v-if="column.sort" class="d-flex flex-column cursor-pointer border rounded-1 mx-2">
                      <ChevronUp
                        class="icon w-3 h-3"
                        @click="handleSort(column.value)"
                        :stroke="sortState.column === column.value
                          && sortState.direction === 'asc'
                          ? 'black'
                          : 'gray'"
                      />
                      <ChevronDown
                        class="icon w-3 h-3"
                        @click="handleSort(column.value)"
                        :stroke="sortState.column === column.value
                          && sortState.direction === 'desc'
                          ? 'black'
                          : 'gray'"
                      />
                    </div>
                  </div>
                </th>
              </tr>
            </thead>

            <template v-if="isLoadingAccounts">
              <tbody>
                <tr v-for="(_, index) in range(pageSize + 1)" :key="index">
                  <td v-for="i in range(TABLE_COLUMNS.length + 1)" :key="i">
                    <div class="placeholder-glow" style="height: 19.5px">
                      <span class="placeholder col-12" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>

            <tbody v-else>
              <template v-if="sortedAccounts.length">
                <tr v-for="account in sortedAccounts" :key="account.account_key">
                  <td>
                    <div class="form-check">
                      <input
                        :id="`checkbox-${account.account_key}`"
                        :checked="selectedAccounts.includes(account.account_key)"
                        @change="handleAccountSelect(account.account_key)"
                        type="checkbox"
                        class="form-check-input"
                      />
                      <label
                        :for="`checkbox-${account.account_key}`"
                        class="form-check-label visually-hidden"
                      >
                        Selecionar conta {{ account.account_key }}
                      </label>
                    </div>
                  </td>
                  <td>
                    <span class="text-secondary">{{ account.account_key }}</span>
                  </td>
                  <td>
                    <b class="text-reset">{{ account.partner_id }}</b>
                  </td>
                  <td class="col-cnpj">
                    <span class="text-secondary">{{ formatCNPJForDisplay(account.partner_cnpj) }}</span>
                  </td>
                  <td class="col-nome">
                    <span
                      class="text-secondary"
                      :title="account.partner_name"
                    >
                      {{ formatNameForDisplay(account.partner_name) }}
                    </span>
                  </td>
                  <td class="col-email">
                    <span class="text-secondary">{{ account.partner_email }}</span>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="`bg-${getProcessingStatusColorClass(account.processing_status)}-lt`"
                    >
                      {{ getProcessingStatusLabel(account.processing_status) }}
                    </span>
                  </td>
                  <td class="col-telefone">
                    <span class="text-secondary">{{ formatPhoneForDisplay(account.partner_phone) }}</span>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="`bg-${getSolfacilStatusColorClass(account.status_solfacil)}-lt`"
                    >
                      {{ getSolfacilStatusLabel(account.status_solfacil) }}
                    </span>
                  </td>
                  <td>
                    <span class="text-secondary">{{
                      account.inserted_at ? date(account.inserted_at).format(DATE_FORMATS.DATETIME) : '-'
                    }}</span>
                  </td>
                  <td>
                    <span class="text-secondary">{{
                      account.updated_at ? date(account.updated_at).format(DATE_FORMATS.DATETIME) : '-'
                    }}</span>
                  </td>
                  <td>
                    <span class="text-secondary">{{ account.approver_email || '-' }}</span>
                  </td>
                  <td>
                    <span class="text-secondary">{{
                      account.last_simulation_date
                        ? date(account.last_simulation_date).format(DATE_FORMATS.DATETIME) : '-'
                    }}</span>
                  </td>
                  <td>
                    <span class="text-secondary">{{
                      account.last_transfer_date
                        ? date(account.last_transfer_date).format(DATE_FORMATS.DATETIME) : '-'
                    }}</span>
                  </td>
                  <td>
                    <span class="text-secondary">{{
                      formatCompetencyMonth(account.competency_month)
                    }}</span>
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr>
                  <td :colspan="TABLE_COLUMNS.length + 1">
                    <div class="d-flex justify-content-center">
                      <span>Nenhuma conta encontrada</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="d-flex justify-content-end gap-2 my-3 pe-5 w-full">
          <div class="d-flex">
            <label class="form-label w-100" for="page_size">Itens por página</label>
            <select
              id="page_size"
              class="form-select"
              :value="pageSize"
              @change="handlePageSizeChange"
            >
              <option v-for="[value, label] in Object.entries(PAGE_SIZE_OPTIONS)" :value="value" :key="value">
                {{ label }}
              </option>
            </select>
          </div>
          <div class="d-flex gap-3">
            <button
              type="button"
              class="btn btn-secondary"
              @click="handleSetPage(Number(apiParams.page) - 1)"
              :disabled="Number(apiParams.page) === 1"
            >
              <ArrowLeft height="12" /> Anterior
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="handleSetPage(Number(apiParams.page) + 1)"
              :disabled="!hasNext"
            >
              Próxima
              <ArrowRight height="12" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showConfirmModal" class="modal modal-blur fade show" style="display: block" tabindex="-1">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title">
              Confirmar {{ pendingAction === 'approve' ? 'Aprovação' : 'Reprovação' }}
            </div>
            <p class="text-secondary">
              Esta operação não pode ser desfeita. Deseja continuar?
            </p>
            <p class="fw-bold">
              {{ selectedAccounts.length }} contas(s) selecionada(s)
            </p>
          </div>
          <div class="modal-footer">
            <div class="w-100">
              <div class="row">
                <div class="col">
                  <button
                    type="button"
                    class="btn w-100"
                    @click="showConfirmModal = false; pendingAction = null"
                    :disabled="isProcessing"
                  >
                    Cancelar
                  </button>
                </div>
                <div class="col">
                  <button
                    type="button"
                    :class="{
                      'btn-success': pendingAction === 'approve',
                      'btn-danger': pendingAction === 'reject',
                    }"
                    class="btn w-100"
                    @click="confirmAction"
                    :disabled="isProcessing"
                  >
                    <template v-if="isProcessing">
                      <div class="spinner-border spinner-border-sm me-2" role="status" />
                      Processando...
                    </template>
                    <template v-else>
                      Confirmar {{ pendingAction === 'approve' ? 'Aprovação' : 'Reprovação' }}
                    </template>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<style scoped lang="scss">
$min-table-height: 50vh;
$hover-opacity: 0.5;

@mixin column-base($min-width, $width) {
  min-width: $min-width;
  width: $width;
  white-space: nowrap;
}

@mixin column-with-ellipsis($min-width, $width) {
  @include column-base($min-width, $width);
  text-overflow: ellipsis;
  overflow: hidden;
}

.card {
  .table-responsive {
    min-height: $min-table-height;
    overflow-x: auto;
    overflow-y: auto;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.break-anywhere {
  word-break: break-word;
}

li {
  &:hover {
    opacity: $hover-opacity;
  }
}

.table {
  tbody {
    tr {
      background-color: white !important;

      &:hover {
        background-color: #f8f9fa !important;
      }
    }
  }

  th,
  td {
    white-space: nowrap;
  }

  .col-cnpj {
    @include column-base(150px, 150px);
  }

  .col-nome {
    @include column-with-ellipsis(180px, 180px);
  }

  .col-telefone {
    @include column-base(130px, 130px);
  }

  .col-email {
    @include column-with-ellipsis(200px, 200px);
  }
}
</style>
